import axios from "axios";
import { SERVER_URL } from "../ServerLink";
import { ACTIVITY_KEY as SESSION_ACTIVITY_KEY, getIdleLimitMs } from "../../utils/sessionConfig";

const REFRESH_PATH = "auth/refresh";
const LOGOUT_PATH = "auth/logout";

// Every request gets a ceiling so a stalled socket (office NAT dropping an idle
// mapping, wifi blip mid-request) fails into the normal error path instead of
// leaving a page on "Loading..." forever. Long-running calls (roster apply) set
// their own per-request timeout, which overrides this default.
const api = axios.create({
    baseURL: SERVER_URL + "/",
    timeout: 60 * 1000,
});

const REFRESH_KEY = "refreshToken";
const ACCESS_KEY = "accessToken";
const ACCESS_TTL_KEY = "accessExpiresInSec";

function getAccessToken() { return window.localStorage.getItem(ACCESS_KEY) || ""; }
function getRefreshToken() { return window.localStorage.getItem(REFRESH_KEY) || ""; }
function setTokens({ accessToken, refreshToken }) {
    if (accessToken) window.localStorage.setItem(ACCESS_KEY, accessToken);
    if (refreshToken) window.localStorage.setItem(REFRESH_KEY, refreshToken);
}
function clearTokens() {
    window.localStorage.removeItem(ACCESS_KEY);
    window.localStorage.removeItem(REFRESH_KEY);
    window.localStorage.removeItem("authUser");
    window.localStorage.removeItem("role");
    window.localStorage.removeItem(SESSION_ACTIVITY_KEY);
    window.localStorage.removeItem(ACCESS_TTL_KEY);
}
function markActivity() {
    window.localStorage.setItem(SESSION_ACTIVITY_KEY, String(Date.now()));
}
function getIdleMs() {
    const last = parseInt(window.localStorage.getItem(SESSION_ACTIVITY_KEY) || "0", 10) || 0;
    return last ? Date.now() - last : -1;
}

export function isDefinitiveAuthError(error) {
    const status = error && error.response && error.response.status;
    return status === 401 || status === 403;
}

let refreshInFlight = null;

async function doNetworkRefresh() {
    const rt = getRefreshToken();
    if (!rt) throw new Error("NO_REFRESH_TOKEN");
    const res = await axios.post(SERVER_URL + "/" + REFRESH_PATH, { refreshToken: rt, idleMs: getIdleMs() }, { timeout: 15 * 1000 });
    const data = res && res.data;
    if (!data || !data.accessToken || !data.refreshToken) {
        throw new Error("BAD_REFRESH_RESPONSE");
    }
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    if (data.accessExpiresIn) {
        window.localStorage.setItem(ACCESS_TTL_KEY, String(data.accessExpiresIn));
    }
    scheduleProactiveRefresh();
    return data.accessToken;
}

async function runRefreshWithLock() {
    const before = getRefreshToken();
    const task = async () => {
        // Another tab may have refreshed while we waited for the lock.
        const current = getRefreshToken();
        if (current && before && current !== before) {
            scheduleProactiveRefresh();
            return getAccessToken();
        }
        return doNetworkRefresh();
    };
    const locks =
        typeof navigator !== "undefined" && navigator.locks && navigator.locks.request
            ? navigator.locks
            : null;
    if (locks) {
        // A frozen/suspended background tab keeps holding this lock with its JS
        // paused, which would park every other tab's 401-refresh — and with it the
        // page that triggered it — on this await forever. Cap the wait; past the
        // cap, refresh without the lock: the `before !== current` re-check in task()
        // already absorbs a rotation another tab completed meanwhile.
        const signal =
            typeof AbortSignal !== "undefined" && AbortSignal.timeout
                ? AbortSignal.timeout(10 * 1000)
                : undefined;
        try {
            return await locks.request("rdc-token-refresh", signal ? { signal } : {}, task);
        } catch (err) {
            if (signal && err && (err.name === "AbortError" || err.name === "TimeoutError")) {
                return task();
            }
            throw err;
        }
    }
    return task();
}

async function performRefresh() {
    if (refreshInFlight) return refreshInFlight;
    const rt = getRefreshToken();
    if (!rt) return Promise.reject(new Error("NO_REFRESH_TOKEN"));
    refreshInFlight = runRefreshWithLock().finally(() => {
        refreshInFlight = null;
    });
    return refreshInFlight;
}

function forceLogoutRedirect(reason = "session_expired") {
    clearTokens();
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login?reason=" + encodeURIComponent(reason);
    }
}

async function endSession(reason = "session_expired") {
    try {
        const rt = getRefreshToken();
        if (rt) {
            await axios
                .post(SERVER_URL + "/" + LOGOUT_PATH, { refreshToken: rt, reason, idleMs: getIdleMs() }, { timeout: 10 * 1000 })
                .catch(() => {});
        }
    } finally {
        forceLogoutRedirect(reason);
    }
}

// Best-effort activity heartbeat: reports how long since the last real user
// action so the server can tell a genuine idle expiry from a session that
// dropped while the user was active. Only sent while active within the idle
// window; never keeps a session alive on its own (it does not mark activity).
async function sendHeartbeat() {
    try {
        if (!getRefreshToken()) return;
        const idleMs = getIdleMs();
        if (idleMs < 0 || idleMs > getIdleLimitMs()) return;
        await api
            .post("auth/heartbeat", { idleMs, path: window.location.pathname })
            .catch(() => {});
    } catch (_) {}
}

api.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
        config.headers.Authorization = "Bearer " + token;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error && error.config;
        const status = error && error.response && error.response.status;
        const url = (original && original.url) || "";

        if (status !== 401 || !original || original.__isRetry) return Promise.reject(error);
        if (url.includes(REFRESH_PATH) || url.includes("/login") || url.includes("/auth/logout")) {
            if (isDefinitiveAuthError(error)) forceLogoutRedirect();
            return Promise.reject(error);
        }

        try {
            const newAccess = await performRefresh();
            original.__isRetry = true;
            original.headers = original.headers || {};
            original.headers.Authorization = "Bearer " + newAccess;
            return api.request(original);
        } catch (refreshErr) {
            const reason = refreshErr && refreshErr.message;
            if (
                isDefinitiveAuthError(refreshErr) ||
                reason === "NO_REFRESH_TOKEN" ||
                reason === "BAD_REFRESH_RESPONSE"
            ) {
                forceLogoutRedirect();
            }
            return Promise.reject(refreshErr);
        }
    }
);

// Proactively renew the access token shortly before it expires so an active
// user is never interrupted by a 401 mid-request. Only renews while the user is
// still active (within the idle window); idle logout is owned by the
// SessionTimeout component, not this module.
let proactiveTimer = null;
function scheduleProactiveRefresh() {
    if (proactiveTimer) clearTimeout(proactiveTimer);
    const ttlSec = parseInt(window.localStorage.getItem(ACCESS_TTL_KEY) || "0", 10) || 0;
    const ttlMs = ttlSec * 1000;
    // Renew at ~80% of the access-token lifetime (min 30s lead), or every 10 min
    // when the lifetime is unknown.
    const lead = ttlMs > 0
        ? Math.max(Math.floor(ttlMs * 0.8), ttlMs - 60 * 1000)
        : 10 * 60 * 1000;
    proactiveTimer = setTimeout(async () => {
        try {
            const last = parseInt(window.localStorage.getItem(SESSION_ACTIVITY_KEY) || "0", 10);
            const idleFor = Date.now() - last;
            if (getRefreshToken() && idleFor < getIdleLimitMs()) {
                await performRefresh();
                return; // performRefresh reschedules on success
            }
        } catch (_) {
            // A failed renewal surfaces on the next request's 401 → refresh path.
        }
        scheduleProactiveRefresh();
    }, lead);
}

if (typeof window !== "undefined") {
    scheduleProactiveRefresh();

    window.addEventListener("storage", (e) => {
        if ((e.key === ACCESS_KEY || e.key === "authUser") && !e.newValue) {
            forceLogoutRedirect();
        }
    });
}

export const auth = {
    getAccessToken,
    getRefreshToken,
    setTokens,
    clearTokens,
    markActivity,
    forceLogoutRedirect,
    endSession,
    performRefresh,
    sendHeartbeat,
};

export default api;
